import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";

import { Button, ListingTerm } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { BuyerDashboardItem } from "containers/BuyerDashboard/assetAdministrationsPostProcessor";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import theme from "theme";

const getAssetFieldsColumns = (buyerDashboardItem: BuyerDashboardItem) => {
  const heldColumns = [
    {
      title: "Purchase Date",
      value: moment(buyerDashboardItem.purchasedDate).format("MM/DD/YY"),
    },
    {
      title: "Purchase Price",
      value: formatCurrencyAmount(
        buyerDashboardItem.currency,
        buyerDashboardItem.purchasedPrice,
      ),
    },
    {
      title: "Royalties Received",
      value: formatCurrencyAmount(
        buyerDashboardItem.currency,
        buyerDashboardItem.royaltiesReceived,
      ),
    },
    {
      title: "Yield",
      value: !!buyerDashboardItem.totalYield
        ? formatPercentage(parseFloat(buyerDashboardItem.totalYield))
        : EMPTY_VALUE_PLACEHOLDER,
    },
  ];
  const soldColumns = [
    {
      title: "Sale Date",
      value: buyerDashboardItem.saleDate
        ? moment(buyerDashboardItem.saleDate).format("MM/DD/YY")
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      title: "Sale Proceeds",
      value: buyerDashboardItem.saleProceeds
        ? formatCurrencyAmount(
            buyerDashboardItem.currency,
            buyerDashboardItem.saleProceeds,
          )
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      title: "ROI $",
      value: formatCurrencyAmount(
        buyerDashboardItem.currency,
        buyerDashboardItem.returnOnInvestmentAmount,
      ),
    },
    {
      title: "ROI %",
      value: buyerDashboardItem.returnOnInvestmentPercentage
        ? formatPercentage(
            parseFloat(buyerDashboardItem.returnOnInvestmentPercentage),
          )
        : EMPTY_VALUE_PLACEHOLDER,
    },
  ];

  return buyerDashboardItem.isHeld
    ? [...heldColumns, ...soldColumns]
    : [...soldColumns, ...heldColumns];
};

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
    },
    cardHeader: {
      textAlign: "left",
      fontSize: "1.7rem",
      "&:hover": {
        cursor: "pointer",
      },
    },
    cardGridItem: {
      minHeight: "60px",
    },
    columnHeader: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
      paddingBottom: theme.spacing(1),
    },
    tagRow: {
      alignItems: "end",
    },
    updatedText: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
    },
    thinAlert: {
      width: "100%",
      padding: "0px 16px",
      fontSize: "0.8rem",
      alignItems: "center",
      "&:hover": {
        cursor: "pointer",
      },
    },
  }),
);

interface AssetCardProps {
  buyerDashboardItem: BuyerDashboardItem;
  onDetailsClick: (buyerDashboardItem: BuyerDashboardItem) => void;
}

const AssetCard: FC<AssetCardProps> = ({
  buyerDashboardItem,
  onDetailsClick,
}) => {
  const classes = useStyles();

  const assetFieldsColumns = getAssetFieldsColumns(buyerDashboardItem);

  return (
    <Card className={classes.card}>
      <CardHeader
        classes={{ root: classes.cardHeader }}
        title={
          <Box onClick={() => onDetailsClick(buyerDashboardItem)}>
            {`${
              buyerDashboardItem.dealTitle
                ? `${buyerDashboardItem.dealTitle} `
                : ""
            }${
              buyerDashboardItem.previousHubspotDealId
                ? `(Deal #${buyerDashboardItem.previousHubspotDealId})`
                : ""
            }`}
          </Box>
        }
      />
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          className={classes.tagRow}
        >
          <Box>
            <ListingTerm term={buyerDashboardItem.purchasedTerm} />
          </Box>
          <Box>
            <Typography variant="caption" className={classes.updatedText}>
              {`Last Updated: ${moment(buyerDashboardItem.lastUpdate).format(
                "MM/DD/YY",
              )}`}
            </Typography>
          </Box>
        </Box>
        {buyerDashboardItem.hasPaymentStatusUpdate && (
          <Box mt={1}>
            <Alert
              severity="warning"
              className={classes.thinAlert}
              onClick={() => onDetailsClick(buyerDashboardItem)}
            >
              Payment Status Update - Click for more information
            </Alert>
          </Box>
        )}
      </CardContent>
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          {assetFieldsColumns.map((assetField, index) => {
            return (
              <Grid
                item
                xs={6}
                sm={3}
                key={index}
                className={classes.cardGridItem}
              >
                <Box
                  className={classes.columnHeader}
                  data-testid="asset-info-item-title"
                >
                  {assetField.title}
                </Box>
                <Box>{assetField.value}</Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
      <Divider />
      <CardContent>
        <Box mt={1}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => onDetailsClick(buyerDashboardItem)}
          >
            Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssetCard;
